import {
  MarketAccessory,
  MarketButton,
  MarketChoiceButton,
  MarketField,
  MarketInputText,
  MarketSegmentedControl,
  MarketSegment,
  MarketHeader,
} from '@market/react';
import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { MarketArrowLeftIcon, MarketCheckmarkIcon } from '@squareup/market-react/icons';
import type { FulfillmentConfiguration } from '@squareup/shared-types-protos/squareup/channelcontrols/configuration/models.js';
import type { Location } from '@squareup/shared-types-protos/squareup/connect/v2/resources/location.js';
import { useCashAppDefaultBrand } from '../../hooks/useCashApp.js';
import { useUpsertFulfillmentConfiguration, FulfillmentType } from '../../hooks/useFulfillmentConfiguration.js';
import { getPrepTimeNumberForSingleConfig, MAX_PREP_TIME_MINUTES } from '../../util/prep-time-utils.js';
import styles from './prep-time-selection-modal-content.module.css';

export interface PrepTimeSelectionModalContentProps {
  currentFulfillmentConfiguration?: FulfillmentConfiguration;
  currentLocation?: Location;
  onClose: () => void;
  onBack?: () => void;
  onSave: (success: boolean) => void;
}

export function PrepTimeSelectionModalContent({
  currentFulfillmentConfiguration,
  currentLocation,
  onClose,
  onBack,
  onSave,
}: PrepTimeSelectionModalContentProps) {
  const { t } = useTranslation();
  const upsertFulfillmentConfiguration = useUpsertFulfillmentConfiguration();
  const { data: defaultBrandData } = useCashAppDefaultBrand();

  const pickupPrepTimeValue = getPrepTimeNumberForSingleConfig(currentFulfillmentConfiguration?.pickupSettings);
  const deliveryPrepTimeValue = getPrepTimeNumberForSingleConfig(currentFulfillmentConfiguration?.deliverySettings);
  const [pickupPrepTime, setPickupPrepTime] = useState<number | null>(pickupPrepTimeValue);
  const [deliveryPrepTime, setDeliveryPrepTime] = useState<number | null>(deliveryPrepTimeValue);
  const [shouldShowCustomField, setShouldShowCustomField] = useState(false);
  const [activeFulfillmentType, setActiveFulfillmentType] = useState<FulfillmentType>(FulfillmentType.Pickup);
  const [isSaving, setIsSaving] = useState(false);
  const [pickupCustomError, setPickupCustomError] = useState<string>('');
  const [deliveryCustomError, setDeliveryCustomError] = useState<string>('');

  const locationName = currentLocation?.name || '';

  const currentPrepTime = useMemo(() => (activeFulfillmentType === FulfillmentType.Pickup ? pickupPrepTime : deliveryPrepTime), [activeFulfillmentType, pickupPrepTime, deliveryPrepTime]);
  const currentCustomError = useMemo(() => (activeFulfillmentType === FulfillmentType.Pickup ? pickupCustomError : deliveryCustomError), [activeFulfillmentType, pickupCustomError, deliveryCustomError]);

  useEffect(() => {
    if (currentPrepTime === null || ![0, 15, 30, 45].includes(currentPrepTime)) {
      setShouldShowCustomField(true);
    } else {
      setShouldShowCustomField(false);
    }
  }, [currentPrepTime]);

  const renderCheckIcon = (isSelected: boolean) => (isSelected ? <MarketCheckmarkIcon style={{ color: 'white' }} /> : null);

  // show button as selected based on current prep time
  const getSelected = (expectedValue?: number) => {
    if (expectedValue === undefined) {
      return shouldShowCustomField;
    }
    return expectedValue === currentPrepTime;
  };

  // show current custom minutes based on pickup/delivery tab selected
  const getCurrentCustomMinutes = () => {
    if (shouldShowCustomField) {
      return activeFulfillmentType === FulfillmentType.Pickup ? pickupPrepTime?.toString() : deliveryPrepTime?.toString();
    }
    return '';
  };

  // tab selection
  const handleFulfillmentTypeSelect = (type: FulfillmentType) => {
    setActiveFulfillmentType(type);
  };

  // pre-set prep time selection
  const handlePrepTimeSelect = (prepTime: number) => {
    setShouldShowCustomField(false);
    if (activeFulfillmentType === FulfillmentType.Pickup) {
      setPickupPrepTime(prepTime);
      setPickupCustomError('');
    } else {
      setDeliveryPrepTime(prepTime);
      setDeliveryCustomError('');
    }
  };

  // show custom input field
  const handleCustomButtonClick = () => {
    setShouldShowCustomField(true);
    if (activeFulfillmentType === FulfillmentType.Pickup) {
      setPickupPrepTime(null);
    } else {
      setDeliveryPrepTime(null);
    }
  };

  // update custom minutes
  const handleCustomMinutesChange = (value: string) => {
    // Only allow numbers
    const numericValue = Number(value.replace(/\D/g, ''));
    const isOverLimit = numericValue > MAX_PREP_TIME_MINUTES;

    if (activeFulfillmentType === FulfillmentType.Pickup) {
      setPickupPrepTime(numericValue);
      if (isOverLimit) {
        setPickupCustomError(t('prepTime.modal.errorLimit'));
      } else {
        setPickupCustomError('');
      }
    } else {
      setDeliveryPrepTime(numericValue);
      if (isOverLimit) {
        setDeliveryCustomError(t('prepTime.modal.errorLimit'));
      } else {
        setDeliveryCustomError('');
      }
    }
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      onClose();
    }
  };

  const isValid = () => {
    // show active tab with error
    if (pickupCustomError) {
      setActiveFulfillmentType(FulfillmentType.Pickup);
      return false;
    }
    if (deliveryCustomError) {
      setActiveFulfillmentType(FulfillmentType.Delivery);
      return false;
    }

    // if no time entered, show active tab with error
    if (pickupPrepTime === null) {
      setActiveFulfillmentType(FulfillmentType.Pickup);
      setPickupCustomError(t('prepTime.modal.errorInvalidNumber'));
      setShouldShowCustomField(true);
      return false;
    }
    if (deliveryPrepTime === null) {
      setActiveFulfillmentType(FulfillmentType.Delivery);
      setDeliveryCustomError(t('prepTime.modal.errorInvalidNumber'));
      setShouldShowCustomField(true);
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    const hasValidSelections = isValid();
    if (!hasValidSelections) {
      return;
    }

    setIsSaving(true);

    try {
      if (!currentFulfillmentConfiguration) {
        throw new Error('No fulfillment configuration available');
      }

      const ensureValidPickupNumber = Number(pickupPrepTime); // can fix input, turns '009' into 9
      const pickupPrepTimeString = `PT${ensureValidPickupNumber}M`;
      const ensureValidDeliveryNumber = Number(deliveryPrepTime);
      const deliveryPrepTimeString = `PT${ensureValidDeliveryNumber}M`;

      const updatedConfiguration = {
        ...currentFulfillmentConfiguration,
        pickupSettings: {
          ...currentFulfillmentConfiguration.pickupSettings,
          prepTimeDuration: pickupPrepTimeString,
        },
        deliverySettings: {
          ...currentFulfillmentConfiguration.deliverySettings,
          prepTimeDuration: deliveryPrepTimeString,
        },
      };

      const hasChannelId = updatedConfiguration?.entity?.channelId;
      if (!hasChannelId) {
        updatedConfiguration.entity = { ...updatedConfiguration.entity, channelId: defaultBrandData?.brandChannelId };
      }

      await upsertFulfillmentConfiguration.mutateAsync({ configuration: updatedConfiguration });

      onSave(true);
      onClose();
    } catch (_error) {
      onSave(false);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      {/* Header with navigation */}
      {onBack
        ? (
          <MarketHeader showNavigation onMarketHeaderNavigate={() => handleBack()}>
            <MarketButton rank="secondary" slot="navigation" data-testid="prep-time-back-button">
              <MarketAccessory slot="icon">
                <MarketArrowLeftIcon />
              </MarketAccessory>
            </MarketButton>

            <h2>{locationName}</h2>
          </MarketHeader>
        )
        : (
          <MarketHeader>
            <h2>{locationName}</h2>
          </MarketHeader>
        )}

      {/* Modal Content */}
      <div className={styles.modalContent}>
        {/* Pickup/Delivery selector */}
        <div className={styles.fulfillmentSelector}>
          <MarketSegmentedControl
            value={activeFulfillmentType}
            data-testid="fulfillment-selector"
            onMarketSegmentedSelectionDidChange={(e) => handleFulfillmentTypeSelect(e.detail.selectedSegmentValue as FulfillmentType)}
          >
            <MarketSegment value={FulfillmentType.Pickup} data-testid="fulfillment-pickup">
              {t('prepTime.pickup')}
            </MarketSegment>
            <MarketSegment value={FulfillmentType.Delivery} data-testid="fulfillment-delivery">
              {t('prepTime.delivery')}
            </MarketSegment>
          </MarketSegmentedControl>
        </div>

        {/* Top row of 3 buttons: 15 min, 30 min, 45 min */}
        <div key={`time-buttons-${activeFulfillmentType}`} className={styles.topButtonRow}>
          <MarketChoiceButton
            key={`15min-${activeFulfillmentType}-${currentPrepTime}`}
            selected={getSelected(15)}
            className={styles.timeButton}
            data-testid="prep-time-15min"
            onClick={() => handlePrepTimeSelect(15)}
          >
            {renderCheckIcon(getSelected(15))}
            {t('prepTime.mins', { count: 15 })}
          </MarketChoiceButton>

          <MarketChoiceButton
            key={`30min-${activeFulfillmentType}-${currentPrepTime}`}
            selected={getSelected(30)}
            className={styles.timeButton}
            data-testid="prep-time-30min"
            onClick={() => handlePrepTimeSelect(30)}
          >
            {renderCheckIcon(getSelected(30))}
            {t('prepTime.mins', { count: 30 })}
          </MarketChoiceButton>

          <MarketChoiceButton
            key={`45min-${activeFulfillmentType}-${currentPrepTime}`}
            selected={getSelected(45)}
            className={styles.timeButton}
            data-testid="prep-time-45min"
            onClick={() => handlePrepTimeSelect(45)}
          >
            {renderCheckIcon(getSelected(45))}
            {t('prepTime.mins', { count: 45 })}
          </MarketChoiceButton>
        </div>

        {/* Full width Custom prep time button */}
        <MarketChoiceButton
          key={`custom-${activeFulfillmentType}-${currentPrepTime}`}
          selected={getSelected()}
          className={styles.fullWidthButton}
          data-testid="prep-time-custom"
          onClick={() => handleCustomButtonClick()}
        >
          {renderCheckIcon(getSelected())}
          {t('prepTime.customPrepTime')}
        </MarketChoiceButton>

        {/* Custom prep time input - only show when custom is selected */}
        {shouldShowCustomField && (
          <div className={styles.customInputContainer}>
            <MarketField className={styles.customField} invalid={!!currentCustomError}>
              <div className={styles.inputWrapper}>
                <MarketInputText
                  value={getCurrentCustomMinutes()}
                  placeholder={t('prepTime.enterMinutes')}
                  className={styles.customInput}
                  data-testid="prep-time-custom-input"
                  onMarketInputValueChange={(e) => handleCustomMinutesChange(e.detail.value)}
                />
                <div className={styles.inputSuffix}>
                  {t('prepTime.min')}
                </div>
              </div>
              {currentCustomError && (
                <span slot="error">{currentCustomError}</span>
              )}
            </MarketField>
          </div>
        )}

        {/* Full width No prep time button */}
        <MarketChoiceButton
          key={`no-prep-${activeFulfillmentType}-${currentPrepTime}`}
          selected={getSelected(0)}
          className={styles.fullWidthButton}
          data-testid="prep-time-none"
          onClick={() => handlePrepTimeSelect(0)}
        >
          {renderCheckIcon(getSelected(0))}
          {t('prepTime.noPrep')}
        </MarketChoiceButton>
      </div>

      {/* Footer with Cancel and Save buttons */}
      <div className={styles.modalFooter}>
        <MarketButton
          rank="secondary"
          className={styles.footerButton}
          data-testid="prep-time-cancel"
          onClick={() => onClose()}
        >
          {t('common.cancel')}
        </MarketButton>

        <MarketButton
          rank="primary"
          className={styles.footerButton}
          disabled={isSaving || upsertFulfillmentConfiguration.isPending}
          isLoading={isSaving || upsertFulfillmentConfiguration.isPending}
          data-testid="prep-time-save"
          onClick={handleSave}
        >
          {t('common.save')}
        </MarketButton>
      </div>
    </>
  );
}
